import React, { useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Divider, Button, Chip } from '@mui/material';
import AuthContext from "flexbiz-core/components/auth/Context";
import DataListManager from 'flexbiz-core/components/DataListManager'; 

// Icons
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

export default function CustomerCarePage() {
  const history = useHistory();
  const context = useContext(AuthContext);
  const { ma_kh } = useParams();

  // Tạo chuỗi ngày hôm nay định dạng YYYY-MM-DD để gán mặc định cho Input type="date"
  const todayString = new Date().toISOString().split('T')[0];

  const careFields = [
    { field: "ngay_cham_soc", label: "Ngày chăm sóc", type: "Date", required: true },
    { 
      field: "hinh_thuc", label: "Hình thức chăm sóc", type: "Select", required: true,
      options: [
        { label: "Gọi điện thoại", value: "Gọi điện" },
        { label: "Nhắn tin Zalo", value: "Zalo" },
        { label: "Gặp mặt trực tiếp", value: "Gặp mặt" },
        { label: "Gửi Email báo giá", value: "Email" }
      ]
    },
    { 
      field: "ma_dt", label: "Dự án quan tâm", type: "Autocomplete", 
      api: "dmdt", apiCondition: { la_du_an_chu_dau_tu: true }, apiMapping: { value: "ma_dt", label: "ten_dt" } 
    },
    { field: "nhu_cau", label: "Nhu cầu khách hàng (Nếu có cập nhật)", type: "String", multiline: true, maxLength: 500 },
    { field: "noi_dung", label: "Nội dung trao đổi", type: "String", multiline: true, required: true, maxLength: 500 },
    { 
      field: "muc_do_quan_tam", label: "Kết quả / Đánh giá", type: "Select", required: true,
      options: [
        { label: "Chốt Deal / Thành công", value: "Thành công" },
        { label: "Tiềm năng - Cần theo dõi thêm", value: "Đang theo dõi" },
        { label: "Khách từ chối / Không có nhu cầu", value: "Thất bại" }
      ]
    },
    // Ẩn khỏi form nhưng vẫn chứa data ngầm nhờ defaultValues
    { field: "ma_kh", label: "Mã Khách Hàng", type: "String", hideInForm: true },
    { field: "date_created", label: "Ngày tạo log", type: "Date", hideInForm: true }
  ];

  const renderCareCard = (item, index, onEdit, onDelete) => {
    const getStatusColor = (status) => {
      if (status === 'Thành công') return { bg: '#e8f5e9', text: '#2e7d32', icon: <CheckCircleIcon fontSize="small" /> };
      if (status === 'Đang theo dõi') return { bg: '#fff8e1', text: '#f57f17', icon: <PendingActionsIcon fontSize="small" /> };
      return { bg: '#ffebee', text: '#c62828', icon: null };
    };
    const statusTheme = getStatusColor(item.trang_thai);

    return (
      <Card key={item._id || index} sx={{ 
        //mb: 2, 
        borderRadius: 3, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)', 
        borderLeft: `4px solid ${statusTheme.text}` 
      }}>
        <CardContent sx={{ p: '16px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                <SupportAgentIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {item.hinh_thuc || 'Chăm sóc'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.ngay_cham_soc ? new Date(item.ngay_cham_soc).toLocaleDateString('vi-VN') : '---'}
              </Typography>
            </Box>
            <Chip icon={statusTheme.icon} label={item.trang_thai || 'Mới'} size="small" sx={{ bgcolor: statusTheme.bg, color: statusTheme.text, fontWeight: 'bold' }} />
          </Box>

          <Box sx={{ bgcolor: '#f4f6f8', p: 1.5, borderRadius: 2, mb: 1.5 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{item.noi_dung}</Typography>
          </Box>

          {item.ma_du_an && (
            <Typography variant="caption" display="block" sx={{ mb: 1, color: '#007aff', fontWeight: '500' }}>
              📌 Quan tâm dự án: {item.ma_du_an}
            </Typography>
          )}

          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={onEdit} sx={{ textTransform: 'none', color: '#ff9500', minWidth: 'auto', px: 1 }}><EditIcon fontSize="small" sx={{ mr: 0.5 }}/> Sửa</Button>
            <Button size="small" onClick={onDelete} sx={{ textTransform: 'none', color: '#ff3b30', minWidth: 'auto', px: 1 }}><DeleteOutlineIcon fontSize="small" sx={{ mr: 0.5 }}/> Xóa</Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <DataListManager
      title="Lịch sử chăm sóc"
      code="customer_care"
      condition={{ ma_kh: ma_kh, user_created: context.userInfo?.email }} 
      sortOptions={[
        { label: "Chăm sóc gần đây", sort: { ngay_cham_soc: -1 } },
        { label: "Chăm sóc cũ nhất", sort: { ngay_cham_soc: 1 } }
      ]}
      fields={careFields}
      
      // SỬ DỤNG DEFAULT VALUES VỪA TẠO
      defaultValues={{ 
        ma_kh: ma_kh, 
        ngay_cham_soc: todayString,
        trang_thai: "Đang theo dõi" 
      }}

      canAdd={true}
      canEdit={true}
      canDelete={true}
      onBack={() => history.goBack()}
      renderItem={renderCareCard} 
    />
  );
}